import { format } from 'date-fns';

import { Icon as BlueIcon } from '@blueprintjs/core';

import { useRef, useState } from 'react';
import { useMenuState } from '@szhsin/react-menu';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { useToast } from '@hooks';
import { useMutation, useQueryClient } from 'react-query';
import { patchTask } from '@services/queries/tasks';
import { ITaskRawQuery } from '@models/Task.model';
import {
  Container,
  DateButton,
  DateContainer,
  DateIcon,
  DateText,
  Dialog,
  Label,
} from './TaskDate.styles';

const TASKS_DATE_FORMAT = 'MMM d, yyyy';

interface ITaskDate {
  task: ITaskRawQuery;
}

const TaskDate = ({ task }: ITaskDate) => {
  const ref = useRef(null);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [newDate, setNewDate] = useState<Date | null>(task.dueDate);

  const [menuProps, toggleMenu] = useMenuState({
    transition: false,
    unmountOnClose: true,
  });

  const mutation = useMutation(patchTask, {
    onSuccess: async () => {
      toast.success('Task changed successfully');

      queryClient.invalidateQueries(['tasks']);
    },
    onError: () => {
      toast.error('Something got wrong');
    },
  });

  return (
    <Container onClick={e => e.stopPropagation()}>
      <Label
        ref={ref}
        onClick={() => {
          toggleMenu(true);
        }}
      >
        <DateContainer>
          <DateIcon>
            <BlueIcon icon="calendar" color="#8F95B2" size={12} />
          </DateIcon>
          <DateText hasDate={!!task.dueDate}>
            {task.dueDate
              ? format(task.dueDate, TASKS_DATE_FORMAT)
              : 'Set Date'}
          </DateText>
        </DateContainer>
        <DateButton hasDate={!!task.dueDate}>
          <BlueIcon
            icon="refresh"
            color={task.dueDate ? '#1371FE' : '#8F95B2'}
            size={10}
          />
        </DateButton>
      </Label>

      <Dialog
        {...menuProps}
        align="center"
        anchorRef={ref}
        onClose={() => {
          toggleMenu(false);
          setNewDate(task.dueDate);
        }}
      >
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <DatePicker
            autoOk
            variant="static"
            openTo="date"
            value={newDate}
            onChange={d => {
              mutation.mutate({
                taskId: task.id,
                dueDate: d,
              });
              toggleMenu(false);
              setNewDate(d);
            }}
          />
        </MuiPickersUtilsProvider>
      </Dialog>
    </Container>
  );
};

export { TaskDate };
